// Copyright 2019 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    ComponentBind,
    ComponentUnbind,
    computedFrom,
    containerless,
    customElement,
    Disposable,
} from "aurelia-framework";
import { Compose } from "aurelia-templating-resources";
import { UserSettingsService } from "services/UserManagement/UserSettingsService";
import { IModal } from "../Interfaces/IModal";
import { IWorkflowModuleInstance, IWorkflowModuleModalInstance } from "../Interfaces/IWorkflowModule";
import { WorkflowModuleModalCreator } from "./WorkflowModuleModalCreator";

@customElement("workflow-module-modal-instance")
@containerless
@autoinject
export class WorkflowModuleModalInstance
    implements IWorkflowModuleModalInstance<unknown, unknown>, ComponentBind, ComponentUnbind
{
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public useHeader = true;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public modalCreator: WorkflowModuleModalCreator<unknown, unknown>;

    @computedFrom("moduleIsBeingDisposed")
    public get isOpen(): boolean {
        return !this.moduleIsBeingDisposed;
    }

    public moduleCollapsed: boolean;

    public modalCompose: Compose;

    private get modalReference(): IModal<unknown, unknown> {
        if (this.modalCompose) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (this.modalCompose as any).currentViewModel;
        }
    }

    private observerSubscriptions: Disposable[] = [];

    private instanceLifetimePromise: Promise<void>;
    private moduleIsBeingDisposed = false;
    private closedPromise: Promise<void>;
    private closedPromiseResolve: (value?: void | PromiseLike<void>) => void;

    public lock = false;

    public get parentModule(): IWorkflowModuleInstance {
        if (this.modalCreator) {
            return this.modalCreator.parentModule;
        }

        return undefined;
    }

    public constructor(
        private readonly userSettingsService: UserSettingsService,
        private readonly bindingEngine: BindingEngine
    ) {
        this.closedPromise = new Promise((resolve, reject) => {
            this.closedPromiseResolve = resolve;
        });
    }

    public bind(): void {
        this.instanceLifetimePromise = new Promise((resolve, reject) => {
            // We're doping a one shot observer
            const composeObserver = this.bindingEngine
                .propertyObserver(this.modalCompose, "currentViewModel")
                .subscribe(() => {
                    // Check for user settings
                    this.userSettingsService
                        .bindUserSettingsProperty(this.modalReference)
                        .then((x) => this.observerSubscriptions.push(x))
                        .then(() => {
                            if (this.modalReference && this.modalReference.activateModal) {
                                return this.modalReference.activateModal(this.modalCreator.state, this);
                            }
                        })
                        .then(() => {
                            if (this.modalCreator && this.modalCreator.modalCallbacks) {
                                for (const callbackName in this.modalCreator.modalCallbacks) {
                                    this.modalReference[callbackName] = this.modalCreator.modalCallbacks[callbackName];
                                }
                            }

                            this.modalCreator.showCompleted(this);
                            composeObserver.dispose();
                            resolve();
                        });
                });
        });
    }

    public unbind(): void {
        if (!this.moduleIsBeingDisposed) this.cancel();

        // Wait until bind call is completed
        this.instanceLifetimePromise = this.instanceLifetimePromise.then(() => {
            this.observerSubscriptions.forEach((x) => x.dispose());
            this.closedPromiseResolve();
        });
    }

    public waitForClose(): Promise<void> {
        return this.closedPromise;
    }

    public lockModal(lock = true): void {
        this.lock = lock;
    }

    public close(): Promise<void> {
        if (!this.moduleIsBeingDisposed) {
            this.moduleIsBeingDisposed = true;

            let newState: unknown;
            let promise = Promise.resolve();
            if (this.modalReference.close) {
                promise = Promise.resolve(this.modalReference.close()).then((state) => {
                    newState = state;
                });
            }

            return promise.then(() => {
                if (this.modalCreator.closeCallbacks) {
                    for (const callback of this.modalCreator.closeCallbacks) callback(newState);
                }
            });
        }
    }

    public cancel(): Promise<void> {
        if (!this.moduleIsBeingDisposed) {
            this.moduleIsBeingDisposed = true;

            let promise = Promise.resolve();
            if (this.modalReference && this.modalReference.cancel) {
                promise = Promise.resolve(this.modalReference.cancel());
            }

            return promise.then(() => {
                if (this.modalCreator && this.modalCreator.cancelCallbacks) {
                    for (const callback of this.modalCreator.cancelCallbacks) callback();
                }
            });
        }
    }

    public updateState(state: unknown): void {
        if (this.modalReference && this.modalReference.updateState) this.modalReference.updateState(state);
    }

    public dispatch<T>(action: () => T | Promise<T>): Promise<T> {
        const promise = this.instanceLifetimePromise.then(() => {
            if (this.moduleIsBeingDisposed) {
                return Promise.reject<T>(undefined);
            }

            return action();
        });

        this.instanceLifetimePromise = promise.catch(() => {}).then(() => {});

        return promise;
    }
}
